import React, { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { LuEye, LuEyeOff } from "react-icons/lu";
import AuthLayout, { AuthField } from "../components/auth/AuthLayout";
import { useLanguage } from "../context/LanguageContext";
import { resetPassword } from "../api/auth";
import "../styles/Auth.scss";

const ResetPasswordPage = () => {
  const { t } = useLanguage();
  const [searchParams] = useSearchParams();
  const token = useMemo(() => searchParams.get("token") ?? "", [searchParams]);
  const hasToken = token.trim().length > 0;
  const PASSWORD_MIN_LENGTH = 9;
  const PASSWORD_MAX_LENGTH = 64;
  const passwordPattern = /^(?=.*[A-Z])(?=.*\d)(?=.*[^\w\s]).{9,}$/;
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [status, setStatus] = useState("idle"); // idle | submitting | success | error
  const [errorMessage, setErrorMessage] = useState("");
  const [passwordVisibility, setPasswordVisibility] = useState({
    password: false,
    confirm: false,
  });

  const handleSubmit = async (event) => {
    event.preventDefault();
    setErrorMessage("");

    if (!hasToken) {
      setErrorMessage(t("auth.resetPassword.missingToken", "Reset token is missing."));
      return;
    }

    const trimmedPassword = password.trim();

    if (!trimmedPassword) {
      setErrorMessage(t("auth.resetPassword.missingPassword", "Please enter a new password."));
      return;
    }

    if (trimmedPassword.length < PASSWORD_MIN_LENGTH) {
      setErrorMessage(
        t(
          "auth.resetPassword.shortPassword",
          "Password must be at least 9 characters."
        )
      );
      return;
    }

    if (!passwordPattern.test(trimmedPassword)) {
      setErrorMessage(
        t(
          "auth.resetPassword.weakPassword",
          "Password must include an uppercase letter, a number, and a special character."
        )
      );
      return;
    }

    if (trimmedPassword !== confirm) {
      setErrorMessage(t("auth.resetPassword.mismatch", "Passwords do not match."));
      return;
    }

    try {
      setStatus("submitting");
      await resetPassword(token.trim(), trimmedPassword);
      setStatus("success");
    } catch (error) {
      setStatus("error");
      setErrorMessage(
        error?.message ||
          t("auth.resetPassword.errorMessage", "Something went wrong. Please try again.")
      );
    }
  };

  const isSubmitting = status === "submitting";
  const showSuccess = status === "success";
  const buildPasswordToggleLabel = (isVisible) =>
    isVisible
      ? t("auth.password.hide", "Hide password")
      : t("auth.password.show", "Show password");

  const togglePasswordVisibility = (field) => {
    setPasswordVisibility((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  if (!hasToken) {
    return (
      <AuthLayout
        eyebrow={t("auth.resetPassword.eyebrow", "Reset access")}
        title={t("auth.resetPassword.title", "Set a new password")}
        lead={t(
          "auth.resetPassword.lead",
          "Create a new password for your Wayli account."
        )}
        illustration={
          <img
            src="/assets/pos_recover.jpg"
            alt={t("auth.media.resetAlt", "Wayli user resetting password")}
            className="auth-illustration"
          />
        }
        asideVariant="solid"
        footer={
          <p>
            {t("auth.resetPassword.footer.text", "Remember your password?")}{" "}
            <Link to="/login">{t("auth.resetPassword.footer.link", "Back to login")}</Link>
          </p>
        }
      >
        <div className="auth-feedback auth-feedback--error" role="alert">
          <h2>{t("auth.resetPassword.missingHeading", "Reset link is missing or invalid")}</h2>
          <p>{t("auth.resetPassword.missingMessage", "Please request a new password reset link.")}</p>
          <Link to="/forgot-password" className="auth-link-inline">
            {t("auth.resetPassword.missingCta", "Request a new link")}
          </Link>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      eyebrow={t("auth.resetPassword.eyebrow", "Reset access")}
      title={t("auth.resetPassword.title", "Set a new password")}
      lead={t(
        "auth.resetPassword.lead",
        "Create a new password for your Wayli account."
      )}
      illustration={
        <img
          src="/assets/pos_recover.jpg"
          alt={t("auth.media.resetAlt", "Wayli user resetting password")}
          className="auth-illustration"
        />
      }
      asideVariant="solid"
      footer={
        <p>
          {t("auth.resetPassword.footer.text", "Remember your password?")}{" "}
          <Link to="/login">{t("auth.resetPassword.footer.link", "Back to login")}</Link>
        </p>
      }
    >
      {showSuccess ? (
        <div className="auth-feedback auth-feedback--success" role="status">
          <h2>{t("auth.resetPassword.successHeading", "Password updated")}</h2>
          <p>{t("auth.resetPassword.successMessage", "You can now sign in with your new password.")}</p>
          <Link to="/login" className="auth-link-inline">
            {t("auth.resetPassword.successCta", "Go to login")}
          </Link>
        </div>
      ) : (
        <form className="auth-form" onSubmit={handleSubmit}>
          <AuthField
            id="reset-password-new"
            label={t("auth.resetPassword.fields.newPassword", "New password")}
            type={passwordVisibility.password ? "text" : "password"}
            name="newPassword"
            autoComplete="new-password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            required
            maxLength={PASSWORD_MAX_LENGTH}
            endAdornment={
              <button
                type="button"
                className="auth-field__password-toggle"
                onClick={() => togglePasswordVisibility("password")}
                aria-label={buildPasswordToggleLabel(passwordVisibility.password)}
                aria-pressed={passwordVisibility.password}
                title={buildPasswordToggleLabel(passwordVisibility.password)}
              >
                {passwordVisibility.password ? (
                  <LuEyeOff aria-hidden="true" />
                ) : (
                  <LuEye aria-hidden="true" />
                )}
              </button>
            }
          />

          <AuthField
            id="reset-password-confirm"
            label={t("auth.resetPassword.fields.confirmPassword", "Confirm password")}
            type={passwordVisibility.confirm ? "text" : "password"}
            name="confirmPassword"
            autoComplete="new-password"
            value={confirm}
            onChange={(event) => setConfirm(event.target.value)}
            required
            maxLength={PASSWORD_MAX_LENGTH}
            endAdornment={
              <button
                type="button"
                className="auth-field__password-toggle"
                onClick={() => togglePasswordVisibility("confirm")}
                aria-label={buildPasswordToggleLabel(passwordVisibility.confirm)}
                aria-pressed={passwordVisibility.confirm}
                title={buildPasswordToggleLabel(passwordVisibility.confirm)}
              >
                {passwordVisibility.confirm ? (
                  <LuEyeOff aria-hidden="true" />
                ) : (
                  <LuEye aria-hidden="true" />
                )}
              </button>
            }
          />

          {status === "error" && (
            <p className="auth-feedback auth-feedback--error" role="alert">
              {errorMessage ||
                t("auth.resetPassword.errorMessage", "Something went wrong. Please try again.")}
            </p>
          )}

          <button type="submit" className="auth-button auth-button--primary" disabled={isSubmitting}>
            {isSubmitting
              ? t("auth.resetPassword.submit.saving", "Saving...")
              : t("auth.resetPassword.submit.default", "Update password")}
          </button>
          {status === "error" && (
            <Link to="/forgot-password" className="auth-link-inline">
              {t("auth.resetPassword.resendCta", "Request a new reset link")}
            </Link>
          )}
        </form>
      )}
    </AuthLayout>
  );
};

export default ResetPasswordPage;
